import { useEffect, useRef, useState } from 'react';
import { useTexture } from '../../hooks/media/useTexture';
import { Project } from '../../types';
import { Mesh, ShaderMaterial } from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Html } from '@react-three/drei';

interface GalleryItemProps {
  project: Project;
  index: number;
  originalIndex: number;
  totalProjects: number;
  material: ShaderMaterial;
  imageScale: [number, number];
  isSelected: boolean;
  onSelect: () => void;
  onHover: (isHovered: boolean) => void;
  transitionProgress: number;
}

export const GalleryItem: React.FC<GalleryItemProps> = ({
  project,
  originalIndex,
  material,
  imageScale,
  onSelect,
  onHover,
  transitionProgress,
  isSelected,
  totalProjects,
}) => {
  const meshRef = useRef<Mesh>(null);
  const scrollRef = useRef<number | null>(null);
  const [width, height] = useTexture(material, project.thumbnail);
  const [isHovered, setIsHovered] = useState(false);

  // Calculer le ratio et les dimensions finales
  const aspectRatio = width / height;
  const finalHeight = imageScale[1];
  // Ajuster la largeur en conservant le ratio d'aspect tout en tenant compte de la taille de l'écran
  const finalWidth = finalHeight * aspectRatio;

  // Limiter la taille maximale sur les petits écrans
  const maxWidth = window.innerWidth * 0.8; // 80% de la largeur de l'écran
  const maxHeight = window.innerHeight * 0.8; // 80% de la hauteur de l'écran

  const scaleFactor = Math.min(
    maxWidth / finalWidth,
    maxHeight / finalHeight,
    1
  );

  const adjustedWidth = finalWidth * scaleFactor;
  const adjustedHeight = finalHeight * scaleFactor;

  useEffect(() => {
    if (material?.uniforms) {
      if (
        material.uniforms.transitionProgress &&
        material.uniforms.time &&
        material.uniforms.scrollOffset
      ) {
        material.uniforms.transitionProgress.value = transitionProgress;
        material.uniforms.time.value = performance.now() * 0.001;
        material.uniforms.scrollOffset.value = scrollRef.current ?? 0;
      } else {
        console.error('Some uniforms are undefined', material.uniforms);
      }
    } else {
      console.error('Material or its uniforms are undefined', material);
    }
  }, [material, transitionProgress, scrollRef]);

  const handlePointerEvents = {
    onClick: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        onSelect();
      }
    },
    onPointerOver: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        onHover(true);
        setIsHovered(true);
        document.body.style.cursor = 'pointer';
      }
    },
    onPointerOut: (e: ThreeEvent<MouseEvent>) => {
      if (!isSelected) {
        e.stopPropagation();
        onHover(false);
        setIsHovered(false);
        document.body.style.cursor = 'auto';
      }
    },
  };

  const getDisplayIndex = (originalIndex: number, totalProjects: number) => {
    return totalProjects - originalIndex;
  };

  return (
    <group {...handlePointerEvents}>
      <mesh ref={meshRef}>
        <planeGeometry args={[adjustedWidth, adjustedHeight, 10, 10]} />
        <primitive object={material} attach="material" />
      </mesh>
      {(isHovered || window.innerWidth < 1024) && !isSelected && (
        <group rotation={meshRef.current?.rotation ?? [0, 0, 0]}>
          <Html
            position={[0, -adjustedHeight / 2 - 0.3, -0.01]}
            center
            className="pointer-events-none"
            transform
            rotation={[0, Math.PI, 0]}
            occlude
            zIndexRange={[0, 100]}
            renderOrder={-1}
          >
            <div className="flex items-baseline justify-between w-[150px] opacity-0 transform translate-y-2 animate-fade-in bg-white/70 px-2 py-1 rounded-lg">
              <span className="text-[8px] font-light text-black">
                {project.title}
              </span>{' '}
              <span className="text-black/40 text-[6px]">
                {getDisplayIndex(originalIndex, totalProjects)}/{totalProjects}
              </span>
            </div>
          </Html>
        </group>
      )}
    </group>
  );
};
